import React, { useMemo, useState } from "react";
import { Autocomplete, Button, RadioGroup, useSnackBar } from "@truevault/aquilo";
import type { AutocompleteItem } from "@truevault/aquilo/src/components/textInput/Autocomplete";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { isEligibleSharingVendor } from "../../../surveys/sharingSurvey";
import {
  useCreateManagedAdNetwork,
  useDeleteManagedAdNetworkByIds,
  useManagedAdNetworksByManager,
} from "../../../hooks/useManagedAdNetworks";
import type { VendorInventoryItem } from "./vendorInventoryUtils";

/**
 * The manager side of "custom audiences managed by another vendor" — asked on a vendor like Klaviyo,
 * while ManagedByVendorSection asks the same thing from the ad network. Both write
 * organization_managed_ad_network with `syncSurvey`, which keeps the selling & sharing survey's
 * custom-audience manager list in step; that list is what nests the ad networks under their manager
 * in the DSAR workflows (see vendorNesting.ts). "No" is simply having no links, so it isn't stored.
 */
export function ManagesAdNetworksSection({
  radioGroupNamePrefix,
  org,
  vendor,
  allVendors,
  portalAutocompleteDropdown = false,
  className,
}: {
  radioGroupNamePrefix: string;
  org: OrganizationDto;
  vendor: VendorInventoryItem;
  allVendors: VendorInventoryItem[];
  portalAutocompleteDropdown?: boolean;
  className?: string;
}) {
  const managerId = parseInt(vendor.recipientData?.id ?? "");

  const { data: managedNetworks = [], isLoading } = useManagedAdNetworksByManager(
    org.id,
    managerId,
  );
  const { mutateAsync: createManagedAdNetwork } = useCreateManagedAdNetwork(org.id);
  const { mutateAsync: deleteManagedAdNetworkByIds } = useDeleteManagedAdNetworkByIds(org.id, {
    syncSurvey: true,
  });
  const showSnackbar = useSnackBar();

  const removeLink = async (networkId: string) => {
    try {
      await deleteManagedAdNetworkByIds({ managerId, networkId: parseInt(networkId) });
      return true;
    } catch {
      showSnackbar("Couldn't remove the ad network. Please try again.", { variant: "error" });
      return false;
    }
  };

  // "Yes" has to show the picker before the first network is added, so intent lives locally.
  const [intent, setIntent] = useState<boolean | null>(null);
  const managesNetworks = isLoading ? null : intent ?? managedNetworks.length > 0;
  // Key to reset the Autocomplete input after each selection.
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  // Same eligibility as the network-side picker shows its section: a vendor you share data with
  // that uses custom audiences.
  const autocompleteItems: AutocompleteItem[] = useMemo(() => {
    const linkedNetworkIds = new Set(managedNetworks.map((link) => link.networkId));
    return allVendors
      .filter(
        (candidate) =>
          candidate.isRecipient &&
          candidate.recipientData &&
          candidate.vendorId !== vendor.vendorId &&
          isEligibleSharingVendor(candidate.recipientData) &&
          candidate.recipientData.usesCustomAudience === true &&
          !linkedNetworkIds.has(candidate.recipientData.id),
      )
      .map((candidate) => ({
        id: candidate.recipientData!.id,
        name: candidate.name,
        logoUrl: candidate.logoUrl,
      }));
  }, [allVendors, managedNetworks, vendor.vendorId]);

  if (Number.isNaN(managerId)) {
    return null;
  }

  return (
    <div className={clsx(className)}>
      <div className="flex justify-between items-start gap-[12px]">
        <div>
          <div className="font-medium text-gray-700">
            {vendor.name} manages custom audience lists for ad networks
          </div>
          <div className="text-neutral-500 text-[14px] leading-[20px] mt-[4px]">
            For example, it syncs your customer list to Meta Ads or Google Ads. Those ad networks
            are then handled through {vendor.name} in your opt-out, deletion and access workflows.
          </div>
        </div>
        <RadioGroup
          name={`${radioGroupNamePrefix}-managesAdNetworks`}
          direction="horizontal"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          selectedValue={managesNetworks === null ? undefined : managesNetworks ? "YES" : "NO"}
          onChange={async (newValue: string) => {
            if (newValue === "YES") {
              setIntent(true);
              return;
            }
            setIntent(false);
            // One at a time: concurrent deletes each miss the other's, so the last one never sees
            // the manager's list empty and the survey keeps listing it as a manager.
            for (const link of managedNetworks) {
              if (!(await removeLink(link.networkId))) {
                // Fall back to what's saved.
                setIntent(null);
                return;
              }
            }
          }}
        />
      </div>

      {managesNetworks === true && (
        <div className="mt-[10px]">
          <Autocomplete
            key={autocompleteKey}
            items={autocompleteItems}
            selectedItem={null}
            // show all possible options initially
            initialSuggestionsCount={autocompleteItems.length}
            onSelect={async (item) => {
              try {
                await createManagedAdNetwork({
                  managerId: managerId.toString(),
                  networkId: item.id,
                  syncSurvey: true,
                });
              } catch {
                showSnackbar(`Couldn't add ${item.name}. Please try again.`, { variant: "error" });
              }
              setAutocompleteKey((k) => k + 1);
            }}
            leadingElement={
              <div className="w-[50px] h-full border border-solid border-neutral-400 rounded-l-sm flex items-center justify-center">
                <MagnifyingGlassIcon className="w-[18px] h-[18px] text-neutral-500" />
              </div>
            }
            leadingInputPaddingPx={66}
            dropdownWidth="w-[417px]"
            portalDropdown={portalAutocompleteDropdown}
          />

          {autocompleteItems.length === 0 && managedNetworks.length === 0 && (
            <div className="mt-[8px] text-[14px] leading-[20px] text-neutral-500">
              Only vendors you share data with that use custom audiences appear here. Set that in
              each ad network&apos;s Targeted Advertising settings.
            </div>
          )}

          {managedNetworks.length > 0 && (
            <div className="mt-[8px] space-y-[6px]">
              {managedNetworks.map((link) => {
                const networkVendor = allVendors.find(
                  (v) => v.isRecipient && v.recipientData?.id === link.networkId,
                );
                return (
                  <div
                    key={link.id}
                    className="flex items-center justify-between border border-solid border-neutral-200 rounded-[4px] px-[12px] py-[8px]"
                  >
                    <div className="flex items-center gap-[10px]">
                      {networkVendor?.logoUrl && (
                        <img
                          src={networkVendor.logoUrl}
                          alt={link.networkName ?? networkVendor.name}
                          className="h-6 w-6"
                        />
                      )}
                      <span className="text-[14px] font-medium text-gray-900">
                        {link.networkName ?? networkVendor?.name ?? "Unknown Vendor"}
                      </span>
                    </div>
                    <Button color="tertiary" size="s" onClick={() => removeLink(link.networkId)}>
                      Remove
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
